"use client";

import {Bar} from "@visx/shape";
import {useEffect, useState} from "react";
import {scaleBand, scaleLinear} from "@visx/scale";
import {AxisBottom, AxisLeft} from "@visx/axis";
import {ParentSize} from "@visx/responsive";
import {instanceMob} from "../../../../../utils/utils.js";
import ConfirmDeletionModal from "../../modal-delete.js";
import {GridColumns, GridRows} from "@visx/grid";
import "./styles.css"

const margin = {top: 20, bottom: 40, left: 70, right: 20};
export const formatMoney = new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
});

export default function BarChart({type = "billing"}) {
    const dataMonth = {
        1: "ENE", 2: "FEB", 3: "MAR", 4: "ABR", 5: "MAY", 6: "JUN",
        7: "JUL", 8: "AGO", 9: "SEP", 10: "OCT", 11: "NOV", 12: "DIC",
    };

    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [modalError, setModalError] = useState<JSX.Element | null>(null);
    const currentYear = new Date().getFullYear();
    const [yearTotal, setYearTotal] = useState(0);
    const [optionYear, setOptionYear] = useState(String(currentYear));
    const [locations, setLocations] = useState<any[]>([]);
    const [locationSelected, setLocationSelected] = useState<string>("-1");
    const [isYearOpen, setIsYearOpen] = useState(false);
    const [isLocationOpen, setIsLocationOpen] = useState(false);

    const dataOptions = Array.from({length: 2}, (_, i) => ({
        id: i + 1,
        name: (currentYear - i).toString(),
        valor: `${currentYear - i}`,
    }));

    const getLocations = () => {
        return instanceMob
            .get(`admin/location/list`)
            .then((res) => res.data)
            .then((data) => setLocations(data))
            .catch((err) => console.error(err));
    };

    async function getData() {
        try {
            setLoading(true);

            let url = "";
            if (type === "billing") {
                url = `admin/metrics/billing?year=${optionYear}`;
                if (locationSelected !== "-1") {
                    url += `&location=${locationSelected}`;
                }
            } else if (type === "joined") {
                url = `admin/metrics/customer/joined?year=${optionYear}`;
                if (locationSelected !== "-1") {
                    url += `&location=${locationSelected}`;
                }
            } else if (type === "left") {
                url = `admin/metrics/customer/left?year=${optionYear}`;
                if (locationSelected !== "-1") {
                    url += `&location=${locationSelected}`;
                }
            }


            if (!url) {
                setData([]);
                return;
            }

            const response = await instanceMob.get(url);
            const apiData = response.data;

            if (type === "billing") {
                if (apiData.monthly && apiData.monthly.length > 0) {
                    const formatted = apiData.monthly.map((ele: any) => ({
                        label: dataMonth[ele.month],
                        value: ele.total_price,
                    }));
                    setData(formatted);
                    setYearTotal(apiData?.year_total?.total_price || 0);
                } else {
                    setData([]);
                }
            } else if (type === "joined") {
                if (Array.isArray(apiData) && apiData.length > 0) {
                    const formatted = apiData.map((ele: any) => ({
                        label: dataMonth[ele.month],
                        value: ele.users_joined,
                    }));
                    setData(formatted);
                    setYearTotal(formatted.reduce((acc, cur) => acc + cur.value, 0));
                } else {
                    setData([]);
                }
            } else if (type === "left") {
                if (Array.isArray(apiData) && apiData.length > 0) {
                    const formatted = apiData.map((ele: any) => ({
                        label: dataMonth[ele.month],
                        value: ele.users_left,
                    }));
                    setData(formatted);
                    setYearTotal(formatted.reduce((acc, cur) => acc + cur.value, 0));
                } else {
                    setData([]);
                }
            }
        } catch (err: any) {
            const msg_error = err?.response?.data;
            if (msg_error) {
                setModalError(
                    <ConfirmDeletionModal
                        onCancel={() => setModalError(null)}
                        onConfirm={() => setModalError(null)}
                        loader={false}
                    >
                        {msg_error?.error || "Algo sucedió, intenta de nuevo"}
                    </ConfirmDeletionModal>
                );
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getData();
    }, [optionYear, type, locationSelected]);

    useEffect(() => {
        getLocations();
    }, []);

    return (
        <div className="p-2 d-flex flex-column gap-2" style={{width: "100%", minHeight: "400px"}}>
            {modalError}
            <div className="w-100 d-flex flex-column gap-3">
                <div className="w-100 d-flex justify-content-between align-items-center h-100">
                    <div
                        className="d-flex justify-content-center align-items-center flex-column w-100 gap-1"
                    >
                        <span className="bar-chart-title">Período {optionYear}</span>
                        {yearTotal > 0 && <span className="bar-chart-subtitle" style={{fontStyle: "italic"}}>Total Acumulado {type !== "billing" ? yearTotal : formatMoney.format(yearTotal)}</span>}
                    </div>
                </div>
                <div className="d-flex justify-content-end gap-2 align-items-center w-100 px-3">

                    <div className="dropdown position-relative text-center">
                        <button
                            className="btn dropdown-toggle card-select d-flex justify-content-between align-items-center"
                            type="button"
                            id="locationDropdown"
                            aria-expanded={isLocationOpen}
                            onClick={() => setIsLocationOpen(!isLocationOpen)}
                        >
                            <span>
                                {locationSelected === "-1"
                                    ? "Todas las sedes"
                                    : locations.find((loc) => String(loc.id) === locationSelected)?.name ||
                                    "Selecciona"}
                            </span>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `<svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.26912 8.89571C6.91484 8.89571 6.56051 8.76057 6.29014 8.49022L0.925015 3.12509C0.38437 2.58444 0.38437 1.70785 0.925015 1.16719C1.46564 0.62656 2.34226 0.62656 2.88289 1.16719L7.26912 5.55347L11.6553 1.16719C12.196 0.62656 13.0726 0.62656 13.6132 1.16719C14.1539 1.70785 14.1539 2.58444 13.6132 3.12509L8.2481 8.49022C7.97775 8.76057 7.6234 8.89571 7.26912 8.89571Z" fill="#9DACBD"/></svg>`,
                                }}
                            />
                        </button>
                        <ul
                            aria-labelledby="locationDropdown"
                            className={`dropdown-menu w-100 card-select-menu p-2 ${
                                isLocationOpen ? "show" : ""
                            }`}
                        >
                            <li
                                key="-1"
                                className={`card-select-label dropdown-item ${
                                    locationSelected === "-1" ? "active" : ""
                                }`}
                                onClick={() => {
                                    setLocationSelected("-1");
                                    setIsLocationOpen(false);
                                }}
                                style={{cursor: "pointer"}}
                            >
                                Todas las sedes
                            </li>
                            {locations.map((loc) => (
                                <li
                                    key={loc.id}
                                    className={`card-select-label dropdown-item ${
                                        locationSelected === String(loc.id) ? "active" : ""
                                    }`}
                                    onClick={() => {
                                        setLocationSelected(String(loc.id));
                                        setIsLocationOpen(false);
                                    }}
                                    style={{cursor: "pointer"}}
                                >
                                    {loc.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="dropdown text-center position-relative">
                        <button
                            className="btn dropdown-toggle card-select year d-flex justify-content-between align-items-center"
                            type="button"
                            id="yearDropdown"
                            aria-expanded={isYearOpen}
                            onClick={() => setIsYearOpen(!isYearOpen)}
                        >
                            <span>
                                {dataOptions.find((d) => d.valor === optionYear)?.name || "Selecciona"}
                            </span>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `<svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.26912 8.89571C6.91484 8.89571 6.56051 8.76057 6.29014 8.49022L0.925015 3.12509C0.38437 2.58444 0.38437 1.70785 0.925015 1.16719C1.46564 0.62656 2.34226 0.62656 2.88289 1.16719L7.26912 5.55347L11.6553 1.16719C12.196 0.62656 13.0726 0.62656 13.6132 1.16719C14.1539 1.70785 14.1539 2.58444 13.6132 3.12509L8.2481 8.49022C7.97775 8.76057 7.6234 8.89571 7.26912 8.89571Z" fill="#9DACBD"/></svg>`,
                                }}
                            />
                        </button>
                        <ul
                            className={`dropdown-menu card-select-menu p-2 year ${
                                isYearOpen ? "show" : ""
                            }`}
                            aria-labelledby="yearDropdown"
                        >
                            {dataOptions.map((item) => (
                                <li
                                    key={item.id}
                                    className={`card-select-label dropdown-item ${
                                        optionYear === item.valor ? "active" : ""
                                    }`}
                                    onClick={() => {
                                        setOptionYear(item.valor);
                                        setIsYearOpen(false);
                                    }}
                                    style={{cursor: "pointer"}}
                                >
                                    {item.name}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

            </div>

            {loading ? (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{width: "100%", flex: 1, minHeight: "400px"}}
                >
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : data ? (
                <div style={{flex: 1, minHeight: "400px", position: "relative"}}>
                    <ParentSize>
                        {({width, height}) => {
                            const safeWidth = Math.max(width, 300);
                            const safeHeight = Math.max(height, 400);

                            const xScale = scaleBand<string>({
                                domain: data.map((d) => d.label),
                                padding: 0.2,
                                range: [margin.left, safeWidth - margin.right],
                            });

                            const yScale = scaleLinear<number>({
                                domain: [0, Math.max(...data.map((d) => d.value))],
                                nice: true,
                                range: [safeHeight - margin.bottom, margin.top],
                            });

                            return (
                                <svg width={safeWidth} height={safeHeight}>

                                    <GridRows
                                        scale={yScale}
                                        width={safeWidth - margin.left - margin.right}
                                        left={margin.left}
                                        stroke="#E0E6ED"
                                        strokeOpacity={0.6}
                                        pointerEvents="none"
                                    />

                                    <GridColumns
                                        scale={xScale}
                                        height={safeHeight - margin.top - margin.bottom}
                                        top={margin.top}
                                        stroke="#E0E6ED"
                                        strokeOpacity={0.6}
                                        pointerEvents="none"
                                    />

                                    <AxisBottom
                                        top={safeHeight - margin.bottom}
                                        scale={xScale}
                                        tickLabelProps={() => ({
                                            textAnchor: "middle",
                                            fill: "#395056",
                                            fontSize: 10,
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            fontFamily: "Work Sans",
                                        })}
                                    />

                                    <AxisLeft
                                        left={margin.left}
                                        scale={yScale}
                                        tickLabelProps={() => ({
                                            textAnchor: "end",
                                            dx: "-0.25em",
                                            dy: "0.25em",
                                            fill: "#A8C2D7",
                                            fontSize: 10,
                                            fontStyle: "normal",
                                            fontWeight: 400,
                                            fontFamily: "Work Sans",
                                        })}
                                    />


                                    {data.map((d) => {
                                        const barWidth = xScale.bandwidth();
                                        const barHeight =
                                            safeHeight - margin.bottom - (yScale(d.value) ?? 0);
                                        const barX = xScale(d.label);
                                        const barY = yScale(d.value);

                                        return (
                                            <g key={d.label}>
                                                <Bar
                                                    x={barX}
                                                    y={barY}
                                                    width={barWidth}
                                                    height={barHeight}
                                                    fill="#007ACC"
                                                />

                                                <text
                                                    x={(barX || 0) + barWidth / 2}
                                                    y={barY - 5}
                                                    textAnchor="middle"
                                                    fontSize="10"
                                                    fill="#395056"
                                                    fontStyle="normal"
                                                    fontWeight="400"
                                                    fontFamily="Work Sans"
                                                >

                                                    {type !== "billing" ? d.value : formatMoney.format(d.value)}
                                                </text>
                                            </g>
                                        );
                                    })}
                                </svg>
                            );
                        }}
                    </ParentSize>
                </div>
            ) : (
                <div style={{flex: 1, minHeight: "400px"}}
                     className="d-flex flex-column align-items-center justify-content-center">
                    <img
                        alt="logo_alta_black_2"
                        style={{padding: "0% 20%"}}
                        src={process.env.PUBLIC_URL + "/assets/img/logo_small_elevva_metrics.png"}
                    />
                    <span>No hay datos</span>
                </div>
            )}
        </div>
    );
}
